// First-time onboarding overlay. Three slides, swipeable on touch + arrow-key
// navigable on desktop. Dismissible from any slide (skip) or completed on the
// last slide (got it). Modeled as a full-bleed cover so it can't be missed.
//
// Triggered from MenuPage when `!hasSeenOnboarding`. Setting the flag is the
// caller's responsibility (so the modal can be replayed later from Settings).

import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { EDGES } from '../theme/tokens';

interface OnboardingModalProps {
  open: boolean;
  onDismiss: () => void;
}

interface Slide {
  eyebrow: string;
  title: string;
  body: string;
  exampleVisual?: React.ReactNode;
}

const SLIDES: Slide[] = [
  {
    eyebrow: 'how it works',
    title: 'Auto-battler basics',
    body:
      "You build a crew in the Tavern, then your crew fights automatically. Position matters — leftmost units attack first. You don't control fights mid-combat. The plan IS the play.",
  },
  {
    eyebrow: 'two crews, one wall',
    title: 'Cholos vs. Emos',
    body:
      "Parking lot at 1am — two crews share the same asphalt. Cholo factions (Plaqueros, La Clica, Bajitos) play tempo and presence. Emo factions (Slameros, La Tocada, Anclas del Backyard) play aggression and resilience. You can mix, but the faction you lean into shapes how your crew feels.",
  },
  {
    eyebrow: 'where the depth lives',
    title: 'Tribes & synergies',
    body:
      "Six tribes (Plaqueros, La Clica, Bajitos · Slameros, La Tocada, Anclas) reward you for stacking units of the same kind. Hit 2, 4, or 6 of one tribe and the whole crew gets stronger. The chip bar in the Tavern shows what's live.",
  },
  {
    eyebrow: 'tavern economy',
    title: 'Reroll, freeze, upgrade',
    body:
      "Reroll the shop for 1g. Freeze it to keep today's lineup for next turn — clutch when you can't afford the unit you want yet. Upgrade your Tier to unlock rarer cards. Manage the gold curve and the rest takes care of itself.",
  },
  {
    eyebrow: 'two more tricks',
    title: 'Triples & hero powers',
    body:
      "Buy three of the same card and they fuse into a golden version with double stats — and the name gets an augmentative (El Placazo, La Doñaza). Heroes also have powers (active or passive) — the rail button glows when yours is ready.",
  },
];

export function OnboardingModal({ open, onDismiss }: OnboardingModalProps) {
  const [idx, setIdx] = useState(0);
  const lastIdx = SLIDES.length - 1;

  // Reset to slide 0 each time the modal opens, so re-triggering shows
  // the first slide rather than wherever the user last navigated.
  useEffect(() => {
    if (open) setIdx(0);
  }, [open]);

  // Keyboard nav — ←/→ to move, ESC to dismiss. Only when open.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowRight') setIdx((i) => Math.min(lastIdx, i + 1));
      else if (e.key === 'ArrowLeft') setIdx((i) => Math.max(0, i - 1));
      else if (e.key === 'Escape') onDismiss();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, lastIdx, onDismiss]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="pt-safe pb-safe fixed inset-0 z-50 flex items-center justify-center bg-asphalt/95 px-lg backdrop-blur-md"
          role="dialog"
          aria-modal="true"
          aria-label="Welcome tutorial"
        >
          {/* Sodium wash for warmth */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse at 30% 20%, rgba(255,138,61,0.16) 0%, rgba(255,138,61,0) 60%)',
            }}
          />

          <motion.div
            initial={{ scale: 0.96, y: 12 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.96, y: 12 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="relative flex w-full max-w-3xl flex-col gap-sm rounded-md border bg-stucco1 p-md md:max-w-3xl md:gap-lg md:p-xl lg:max-w-3xl lg:p-2xl"
            style={{ borderColor: EDGES.mid }}
          >
            {/* Skip — top-right */}
            <button
              onClick={onDismiss}
              className="absolute right-md top-md font-data text-[10px] uppercase tracking-[0.3em] text-graphite hover:text-marker md:text-[11px]"
            >
              skip ×
            </button>

            {/* Slide content with AnimatePresence for crossfade */}
            <AnimatePresence mode="wait">
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.18 }}
                className="flex min-h-[120px] flex-col gap-xs md:min-h-[200px] md:gap-md"
              >
                <p className="font-data text-[10px] uppercase tracking-[0.3em] text-marker md:text-[12px]">
                  {SLIDES[idx].eyebrow}
                </p>
                <h2 className="font-cholo text-[22px] leading-none text-sodium md:text-[36px] lg:text-[48px]">
                  {SLIDES[idx].title}
                </h2>
                <p className="text-[12px] leading-snug text-bone md:text-[16px] lg:text-[18px] lg:leading-relaxed">
                  {SLIDES[idx].body}
                </p>
              </motion.div>
            </AnimatePresence>

            {/* Slide indicator dots */}
            <div className="flex items-center justify-center gap-xs" aria-hidden>
              {SLIDES.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  className={`h-[6px] rounded-full transition-all ${
                    i === idx ? 'w-[24px] bg-sodium' : 'w-[6px] bg-stucco3 hover:bg-marker'
                  }`}
                />
              ))}
            </div>

            {/* Nav buttons */}
            <div className="flex items-center justify-between gap-md">
              <button
                onClick={() => setIdx((i) => Math.max(0, i - 1))}
                disabled={idx === 0}
                className={`min-h-[44px] font-data text-[12px] uppercase tracking-[0.2em] transition-colors md:text-[13px] ${
                  idx === 0
                    ? 'cursor-not-allowed text-graphite'
                    : 'text-marker hover:text-bone'
                }`}
              >
                ← back
              </button>
              {idx < lastIdx ? (
                <motion.button
                  onClick={() => setIdx((i) => i + 1)}
                  whileTap={{ scale: 0.97 }}
                  className="min-h-[44px] rounded-sm bg-stucco2 px-lg font-data text-[12px] uppercase tracking-[0.2em] text-bone hover:bg-stucco3 md:min-h-[48px] md:text-[13px]"
                >
                  Next ›
                </motion.button>
              ) : (
                <motion.button
                  onClick={onDismiss}
                  whileTap={{ scale: 0.97 }}
                  className="min-h-[44px] rounded-sm bg-sodium px-lg font-data text-[12px] uppercase tracking-[0.2em] text-sharpie shadow-sodium hover:bg-sodiumGlow md:min-h-[48px] md:text-[13px]"
                >
                  Got it ›
                </motion.button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
